import React from "react";
import { applyFormat } from "components/ui/ui-helpers";

const defaultTubeColors = ["#3b82f6", "#10b981", "#9ca3af"];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const StackedBarChart = ({ title, data, colors = defaultTubeColors, variant = "simple", uniformHeight = false, referenceValue }) => {
  if (!data || data.length === 0) {
    return null;
  }

  if (variant === "tube") {
    const maxTotal = Math.max(
      ...data.map((item) => toNumber(item.obligatoire) + toNumber(item.facultatif) + toNumber(item.perteRevenu))
    );

    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e5e7eb",
          marginBottom: "20px",
          height: "500px",
        }}
      >
        <h3
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#1f2937",
            marginBottom: "20px",
            textAlign: "center",
          }}
        >
          {title}
        </h3>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-around",
            height: "400px",
            gap: "20px",
            paddingBottom: "40px",
          }}
        >
          {data.map((item, index) => {
            const obligatoire = toNumber(item.obligatoire);
            const facultatif = toNumber(item.facultatif);
            const perteRevenu = toNumber(item.perteRevenu);
            const total = obligatoire + facultatif + perteRevenu;
            const maxHeight = 300;
            const totalHeight = uniformHeight ? maxHeight : maxTotal > 0 ? (total / maxTotal) * maxHeight : 20;
            const obligatoireHeight = total > 0 ? (obligatoire / total) * totalHeight : 0;
            const facultatifHeight = total > 0 ? (facultatif / total) * totalHeight : 0;
            const perteRevenuHeight = total > 0 ? (perteRevenu / total) * totalHeight : 0;
            const refValue = referenceValue !== undefined ? referenceValue : total;
            const obligatoirePct = refValue > 0 ? Math.round((obligatoire / refValue) * 100) : 0;
            const facultatifPct = refValue > 0 ? Math.round((facultatif / refValue) * 100) : 0;
            const perteRevenuPct = refValue > 0 ? Math.round((perteRevenu / refValue) * 100) : 0;

            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: 1,
                  maxWidth: "120px",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: `${totalHeight}px`,
                    position: "relative",
                    borderRadius: "8px",
                    border: "2px solid #e5e7eb",
                    backgroundColor: "rgba(229, 231, 235, 0.1)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    cursor: "pointer",
                    transition: "transform 0.2s ease",
                    marginTop: "20px",
                    overflow: "hidden",
                  }}
                  onMouseEnter={({ currentTarget }) => {
                    currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={({ currentTarget }) => {
                    currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0",
                      left: "0",
                      right: "0",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column-reverse",
                    }}
                  >
                    {obligatoireHeight > 0 && (
                      <div
                        style={{
                          height: `${obligatoireHeight}px`,
                          background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[0]}cc 50%, ${colors[0]}99 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          color: "white",
                          fontWeight: "500",
                          position: "relative",
                          zIndex: 3,
                        }}
                      >
                        {obligatoirePct > 0 ? `${obligatoirePct}%` : ""}
                      </div>
                    )}

                    {facultatifHeight > 0 && (
                      <div
                        style={{
                          height: `${facultatifHeight}px`,
                          background: `linear-gradient(135deg, ${colors[1]} 0%, ${colors[1]}cc 50%, ${colors[1]}99 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          color: "white",
                          fontWeight: "500",
                          position: "relative",
                          zIndex: 2,
                        }}
                      >
                        {facultatifPct > 0 ? `${facultatifPct}%` : ""}
                      </div>
                    )}

                    {perteRevenuHeight > 0 && (
                      <div
                        style={{
                          height: `${perteRevenuHeight}px`,
                          background: `linear-gradient(135deg, ${colors[2]}40 0%, ${colors[2]}20 50%, ${colors[2]}10 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          color: "#6b7280",
                          fontWeight: "500",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        {perteRevenuPct > 0 ? `${perteRevenuPct}%` : ""}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textAlign: "center",
                    marginTop: "8px",
                    lineHeight: "1.2",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {item.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((item) => toNumber(item.value1)));
  const scaleSteps = 5;
  const stepValue = maxValue / scaleSteps;

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        marginBottom: "20px",
        height: "400px",
      }}
    >
      <h3
        style={{
          fontSize: "14px",
          fontWeight: "600",
          color: "#1f2937",
          marginBottom: "20px",
          textAlign: "center",
        }}
      >
        {title}
      </h3>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          height: "300px",
          gap: "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "250px",
            marginRight: "10px",
            marginBottom: "38px",
          }}
        >
          {Array.from({ length: scaleSteps + 1 }, (_, index) => {
            const value = maxValue - index * stepValue;
            return (
              <div
                key={index}
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  textAlign: "right",
                  minWidth: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {applyFormat(value, "uint", { thousands: " ", unit: " €" })}
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-around",
            height: "250px",
            gap: "15px",
            flex: 1,
            marginTop: "20px",
          }}
        >
          {data.map((item, index) => {
            const value = toNumber(item.value1);
            const maxHeight = 250;
            const barHeight = maxValue > 0 ? (value / maxValue) * maxHeight : 10;

            let backgroundColor = "linear-gradient(135deg, rgb(59, 130, 246) 0%, rgb(29, 78, 216) 100%)";
            if (item.category === "Economie d'impôt") {
              backgroundColor = "linear-gradient(135deg, rgb(5, 150, 105) 0%, rgb(4, 120, 87) 100%)";
            } else if (item.category === "Effort réel d'épargne") {
              backgroundColor = "linear-gradient(135deg, rgb(124, 58, 237) 0%, rgb(109, 40, 217) 100%)";
            }

            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: 1,
                  maxWidth: "120px",
                }}
              >
                <div
                  style={{
                    width: "100px",
                    height: `${barHeight}px`,
                    background: backgroundColor,
                    borderRadius: "8px 8px 4px 4px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    cursor: "pointer",
                    transition: "transform 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "10px",
                    fontWeight: "600",
                  }}
                  onMouseEnter={({ currentTarget }) => {
                    currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={({ currentTarget }) => {
                    currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {applyFormat(value, "uint", { thousands: " ", unit: " €" })}
                </div>

                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textAlign: "center",
                    marginTop: "8px",
                    lineHeight: "1.2",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {item.category}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StackedBarChart;
